import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from './cn.js';

export const buttonVariants = cva(
  'inline-flex min-h-11 shrink-0 items-center justify-center gap-2 rounded-sm border text-sm font-semibold transition-colors disabled:pointer-events-none disabled:opacity-45 [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary:
          'border-transparent bg-accent text-accent-ink hover:bg-accent-dim focus-visible:outline-accent',
        secondary:
          'border-border-hi bg-surface text-ink hover:border-accent-line hover:bg-surface-hi',
        ghost: 'border-transparent bg-transparent text-ink-2 hover:bg-surface hover:text-ink',
        danger:
          'border-transparent bg-err text-white hover:bg-[color-mix(in_oklch,var(--err)_82%,black)]',
      },
      size: {
        default: 'px-4 py-2.5',
        small: 'min-h-11 px-3 py-1.5 text-xs',
        icon: 'size-11 p-0',
      },
    },
    defaultVariants: { variant: 'primary', size: 'default' },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ asChild = false, className, size, type = 'button', variant, ...props }, ref) => {
    const Component = asChild ? Slot : 'button';
    return (
      <Component
        ref={ref}
        className={cn(buttonVariants({ size, variant }), className)}
        type={asChild ? undefined : type}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';
